// Cache rebuild trigger
import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../apiConfig';

const CROP_NAMES = {
    // Vegetables
    long_beans: 'මෑ',
    luffa: 'වැටකොළු',
    leeks: 'ලීක්ස්',
    bean: 'බෝංචි',
    tomato: 'තක්කාලි',
    brinjal: 'වම්බටු',
    cabbage: 'ගෝවා',
    capsicum: 'මාළු මිරිස්',
    bitter_gourd: 'කරවිල',
    snake_gourd: 'පතෝල',
    okra: 'බණ්ඩක්කා',
    pumpkin: 'වට්ටක්කා',
    cucumber: 'පිපිඤ්ඤා',
    winged_bean: 'දඹල',
    radish: 'රාබු',
    // Paddy
    paddy: 'වී',

    // Fruits
    mango: 'අඹ',
    papaya: 'පැපොල්',
    pineapple: 'අන්නාසි',
    banana: 'කෙසෙල්',
    rambutan: 'රඹුටන්',
    durian: 'දුරියන්',
    passion: 'පැෂන්',
    passion_fruit: 'පැෂන්',
    orange: 'දොඩම්',
    jamanarang: 'ජමනරංග',

    // OFC
    green_chilli: 'අමු මිරිස්',
    maize: 'බඩ ඉරිගු',
    green_gram: 'මුං ඇට',
    cowpea: 'කවුපි',
    sesame: 'තල',
    finger_millet: 'කුරක්කන්',
    red_onion: 'රතු ලූනු',
    cashew: 'රට කජු',

    // Spices
    chilli: 'මිරිස්',
    pepper: 'කළු මිරිස්',
    turmeric: 'කුරුඩු',
    cardamom: 'එලවත්',
    clove: 'ලවංගා',
    cinnamon: 'සිනමන්',
    // Roots
    innala: 'ඉන්නල',
    cassava: 'මඤ්ඤොක්කා',
    sweetpotato: 'බතල',
    sweet_potato: 'බතල',
    vinetubers: 'වැල් අල',
};

export default function GenericCropPage({ category, title, onBackClick, cropMapping = {} }) {
    const isVegetables = category === 'vegetables';
    const [selectedSeason, setSelectedSeason] = useState('all');
    const [selectedCrop, setSelectedCrop] = useState('all');

    useEffect(() => {
        setSelectedSeason('all');
        setSelectedCrop('all');
    }, [category]);

    const STORAGE_KEY = `crops_${category}_data_public`;
    const [cropsData, setCropsData] = useState(() => {
        const local = localStorage.getItem(STORAGE_KEY);
        try {
            return local ? JSON.parse(local) : [];
        } catch (e) {
            return [];
        }
    });
    const [loading, setLoading] = useState(() => {
        const local = localStorage.getItem(STORAGE_KEY);
        try {
            return (local ? JSON.parse(local) : []).length === 0;
        } catch (e) {
            return true;
        }
    });

    useEffect(() => {
        let timerId;
        let isCancelled = false;

        const loadData = async () => {
            const url = getApiUrl(`api_crops.php?category=${category}&t=${Date.now()}`);

            try {
                const response = await fetch(url, {
                    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                });
                if (isCancelled) return;

                if (response.ok) {
                    const text = await response.text();
                    try {
                        const data = JSON.parse(text);
                        if (Array.isArray(data)) {
                            const sortedData = [...data].sort((a, b) => {
                                if (a.year !== b.year) return String(a.year).localeCompare(String(b.year), undefined, { numeric: true });
                                return (a.crop || '').localeCompare(b.crop || '');
                            });

                            setCropsData(currentData => {
                                const newDataStr = JSON.stringify(sortedData);
                                const currentDataStr = JSON.stringify(currentData);

                                if (newDataStr !== currentDataStr) {
                                    console.log(`🔄 ${category} update detected, refreshing UI...`);
                                    localStorage.setItem(STORAGE_KEY, newDataStr);
                                    return sortedData;
                                }
                                return currentData;
                            });
                        }
                    } catch (parseErr) {
                        console.warn('Response was not JSON:', parseErr);
                    }
                }
            } catch (err) {
                console.warn("Server fetch failed", err);
            }
            if (!isCancelled) setLoading(false);
        };

        // Initial load
        loadData();

        // 📱 Refresh when coming back to the tab
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                loadData();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            isCancelled = true;
            clearTimeout(timerId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [category, STORAGE_KEY]);

    const safeCropsList = Array.isArray(cropsData) ? cropsData : [];

    const seasons = Array.from(
        new Set(safeCropsList.map(item => item && item.year ? String(item.year).trim() : '').filter(Boolean))
    );

    const ALL_VEGETABLE_CROPS = [
        'long_beans', 'luffa', 'bean', 'tomato', 'brinjal', 'capsicum',
        'bitter_gourd', 'snake_gourd', 'okra', 'pumpkin', 'cucumber', 
        'winged_bean', 'radish'
    ];

    const availableCrops = isVegetables 
        ? ALL_VEGETABLE_CROPS
        : Array.from(
            new Set(safeCropsList.map(item => item && item.crop ? String(item.crop).trim() : '').filter(Boolean))
        );

    const displayedData = isVegetables ? safeCropsList.filter(item => {
        if (!item) return false;
        const itemYear = String(item.year || '').trim();
        const itemCrop = String(item.crop || '').trim().toLowerCase();
        const targetCrop = String(selectedCrop || '').trim().toLowerCase();

        const matchesSeason = !selectedSeason || selectedSeason === 'all' || itemYear === selectedSeason;
        const matchesCrop = !selectedCrop || selectedCrop === 'all' || itemCrop === targetCrop;
        return matchesSeason && matchesCrop;
    }) : safeCropsList;

    return (
        <main className="page-content crops-page" style={{ padding: '28px 16px', paddingBottom: '180px', maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                <button
                    className="back-button"
                    onClick={() => onBackClick('crops')}
                    style={{
                        backgroundColor: '#0b6b2f',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    ← ආපසු
                </button>
                <h1 style={{ color: '#0b6b2f', fontSize: 28, marginBottom: 12, textAlign: 'justify' }}>{title}</h1>
                <hr className="footer-sep" />

                {/* 🔍 Filter Bar (එළවළු වගාවේ පමණක් දිස්වේ) */}
                {isVegetables && (
                    <div className="crop-filter-card" style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        padding: '16px 20px',
                        marginBottom: '20px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.04)',
                        border: '1px solid #eef2f6',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', flex: '1 1 auto' }}>
                            {/* Season Filter */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label htmlFor="season-filter-select" style={{ fontWeight: '600', color: '#374151', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                    🌾 කන්නය:
                                </label>
                                <select
                                    id="season-filter-select"
                                    value={selectedSeason}
                                    onChange={(e) => setSelectedSeason(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #d1d5db',
                                        fontSize: '14px',
                                        color: '#1f2937',
                                        background: '#f9fafb',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        minWidth: '150px'
                                    }}
                                >
                                    <option value="all">සියලු කන්න (All)</option>
                                    {seasons.map((season, idx) => (
                                        <option key={idx} value={season}>{season}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Crop Filter */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label htmlFor="crop-filter-select" style={{ fontWeight: '600', color: '#374151', fontSize: '14px', whiteSpace: 'nowrap' }}>
                                    🥦 බෝගය:
                                </label>
                                <select
                                    id="crop-filter-select"
                                    value={selectedCrop}
                                    onChange={(e) => setSelectedCrop(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #d1d5db',
                                        fontSize: '14px',
                                        color: '#1f2937',
                                        background: '#f9fafb',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        minWidth: '160px'
                                    }}
                                >
                                    <option value="all">සියලු බෝග (All)</option>
                                    {availableCrops.map((cropVal, idx) => (
                                        <option key={idx} value={cropVal}>
                                            {CROP_NAMES[cropVal] || cropVal}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Reset Button */}
                            {(selectedSeason !== 'all' || selectedCrop !== 'all') && (
                                <button
                                    type="button"
                                    onClick={() => { setSelectedSeason('all'); setSelectedCrop('all'); }}
                                    style={{
                                        padding: '7px 14px',
                                        borderRadius: '8px',
                                        background: '#f3f4f6',
                                        border: '1px solid #d1d5db',
                                        color: '#4b5563',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    ✕ නැවත සකසන්න
                                </button>
                            )}
                        </div>

                        <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>
                            ප්‍රතිඵල: <span style={{ color: '#0b6b2f', fontWeight: '700' }}>{displayedData.length}</span> ක් හමු විය
                        </div>
                    </div>
                )}

                <div className="table-responsive" style={{
                    background: '#fff',
                    borderRadius: '20px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
                    padding: '24px',
                    border: '1px solid #f0f0f0'
                }}>
                    <table className="production-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: '#f9fafb' }}>
                                <th style={{ padding: '16px', borderBottom: '2px solid #eee', color: '#666' }}>{category === 'fruits' ? 'වර්ෂය' : 'කන්නය'}</th>
                                <th style={{ padding: '16px', borderBottom: '2px solid #eee', color: '#666' }}>බෝගය</th>
                                <th style={{ padding: '16px', borderBottom: '2px solid #eee', color: '#666' }}>වපසරිය (හෙක්)</th>
                                <th style={{ padding: '16px', borderBottom: '2px solid #eee', color: '#666' }}>නිෂ්පාදනය (මෙ.ටො)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>දත්ත පූරණය වෙමින් පවතියි...</td>
                                </tr>
                            ) : displayedData.length > 0 ? (
                                displayedData.map((item, idx) => (
                                    <tr key={idx} style={{ transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = '#fcfcfc'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                                        <td style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', fontWeight: '700', color: '#111827' }}>{item.year}</td>
                                        <td style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>{CROP_NAMES[item.crop] || item.crop}</td>
                                        <td style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>{item.area}</td>
                                        <td style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', color: '#059669', fontWeight: '600' }}>{item.production}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                                        {cropsData.length === 0 ? 'තවමත් දත්ත ඇතුළත් කර නොමැත.' : 'තෝරාගත් පෙරහනට අදාළ දත්ත හමු නොවීය.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                @media (max-width: 640px) {
                    .table-responsive {
                        padding: 12px !important;
                        overflow-x: auto;
                    }
                    .production-table th, .production-table td {
                        padding: 12px 8px !important;
                        font-size: 0.85rem;
                    }
                }
            `}</style>
        </main>
    );
}

